import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Scan, Package, MapPin, CheckCircle, Box } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * Slice D7 — Per-section FG Put-Away clone (Salem-untouched).
 *
 * Flow: staff scan → bundle scan (auto-suggests bin) → pack scan + bin →
 * confirm pack put-away. Progress counter shows N/M packs done. When all
 * packs are put away, the bundle is auto-marked FG_COMPLETED in the
 * per-section SFG inventory.
 */

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

const PerSectionFgPutAwayClone = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug] || slug;
  const backRoute = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/fg-put-away`;

  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const packInputRef = useRef(null);
  const binInputRef = useRef(null);

  const [staffId, setStaffId] = useState('');
  const [staffValidated, setStaffValidated] = useState(false);
  const [staffName, setStaffName] = useState('');

  const [bundleCode, setBundleCode] = useState('');
  const [bundleDetails, setBundleDetails] = useState(null);
  const [suggestedBin, setSuggestedBin] = useState(null);

  const [packBarcode, setPackBarcode] = useState('');
  const [binCode, setBinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [packSuccess, setPackSuccess] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    staffInputRef.current?.focus();
  }, []);

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffValidated(true);
        setStaffName(response.data.staff_name);
        toast.success(`Staff validated: ${response.data.staff_name}`);
        setTimeout(() => bundleInputRef.current?.focus(), 100);
      } else {
        toast.error('Invalid Staff ID');
      }
    } catch (error) {
      toast.error('Staff validation failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestedBin = async (skuCode) => {
    if (!skuCode) return;
    try {
      const response = await apiClient.post(`${apiBase}/suggest-bin/${encodeURIComponent(skuCode)}`);
      setSuggestedBin(response.data.suggested_bin);
      if (response.data.suggested_bin) {
        setBinCode(response.data.suggested_bin.bin_code);
      }
    } catch (error) {
      setSuggestedBin(null);
    }
  };

  const handleBundleScan = async () => {
    if (!bundleCode.trim()) {
      toast.error('Please scan or enter bundle code');
      return;
    }
    try {
      setLoading(true);
      setPackSuccess(false);
      const response = await apiClient.post(
        `${apiBase}/validate-bundle/${encodeURIComponent(bundleCode)}`
      );
      const bundle = response.data.bundle;
      setBundleDetails(bundle);
      setProgress({
        putaway_count: bundle.putaway_count,
        total_packs: bundle.total_packs,
        remaining_packs: bundle.remaining_packs,
      });
      await fetchSuggestedBin(bundle.sku_code);
      toast.success(
        `Bundle ready · ${bundle.remaining_packs} of ${bundle.total_packs} packs left`
      );
      setTimeout(() => packInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle validation failed');
      setBundleDetails(null);
      setSuggestedBin(null);
      setProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPack = async () => {
    if (!bundleDetails) {
      toast.error('Please scan a bundle first');
      return;
    }
    if (!packBarcode.trim()) {
      toast.error('Please scan pack barcode');
      packInputRef.current?.focus();
      return;
    }
    if (!binCode.trim()) {
      toast.error('Please enter bin location');
      binInputRef.current?.focus();
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.post(`${apiBase}/pack`, {
        staff_id: staffId,
        bundle_code: bundleCode,
        pack_barcode: packBarcode,
        bin_code: binCode,
      });
      toast.success(response.data.message);
      setPackSuccess(true);
      setProgress(response.data.progress);
      setTimeout(() => {
        setPackBarcode('');
        setPackSuccess(false);
        if (response.data.progress?.bundle_complete) {
          // Bundle done — reset to bundle scan.
          setBundleCode('');
          setBundleDetails(null);
          setSuggestedBin(null);
          setBinCode('');
          setProgress(null);
          bundleInputRef.current?.focus();
          toast.success('Bundle FG-completed!');
        } else {
          // Same bundle — focus next pack scan.
          packInputRef.current?.focus();
        }
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pack put-away failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') action();
  };

  const resetAll = () => {
    setStaffId('');
    setStaffValidated(false);
    setStaffName('');
    setBundleCode('');
    setBundleDetails(null);
    setSuggestedBin(null);
    setPackBarcode('');
    setBinCode('');
    setProgress(null);
    setPackSuccess(false);
    staffInputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-fg-put-away-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate(backRoute)}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">FG Put-Away</h1>
            <p className="text-sm text-[#E8DCC8]">{sectionName}</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">Staff Validation</h2>
          {!staffValidated ? (
            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Staff ID</Label>
                <Input
                  ref={staffInputRef}
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                  placeholder="Scan or enter Staff ID"
                  className="mt-1"
                  data-testid="staff-id-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleStaffValidate}
                  disabled={loading}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  data-testid="validate-staff-btn"
                >
                  Validate
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">{staffName}</span>
                <span className="text-sm text-green-600">({staffId})</span>
              </div>
              <Button variant="ghost" size="sm" onClick={resetAll} data-testid="change-staff-btn">
                Change
              </Button>
            </div>
          )}
        </div>

        {staffValidated && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">
              <Scan className="w-5 h-5 inline mr-2" />
              Scan Bundle
            </h2>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Bundle Barcode</Label>
                <Input
                  ref={bundleInputRef}
                  value={bundleCode}
                  onChange={(e) => setBundleCode(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleBundleScan)}
                  placeholder="Scan bundle barcode"
                  className="mt-1"
                  data-testid="bundle-code-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleBundleScan}
                  disabled={loading || !bundleCode}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  data-testid="validate-bundle-btn"
                >
                  Validate
                </Button>
              </div>
            </div>

            {bundleDetails && progress && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-[#1A1F2E]">Bundle: {bundleDetails.bundle_code}</h3>
                  <span className="text-sm font-semibold px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                    {progress.putaway_count}/{progress.total_packs} packs
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#5A5F6E]">SKU:</span>
                    <span className="ml-2 font-medium">{bundleDetails.sku_code || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Velocity:</span>
                    <span className="ml-2 font-bold">{bundleDetails.velocity_class}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-[#5A5F6E]">Design:</span>
                    <span className="ml-2 font-medium">{bundleDetails.design_name || '—'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {bundleDetails && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">
              <Package className="w-5 h-5 inline mr-2" />
              Put Away Pack
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Pack Barcode (USN)</Label>
                <Input
                  ref={packInputRef}
                  value={packBarcode}
                  onChange={(e) => setPackBarcode(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, () => binInputRef.current?.focus())}
                  placeholder="Scan pack barcode"
                  className="mt-1"
                  data-testid="pack-barcode-input"
                />
              </div>

              {suggestedBin && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-600" />
                    <span className="text-green-800">
                      Suggested Bin: <strong>{suggestedBin.bin_code}</strong>
                    </span>
                  </div>
                  <span className="text-sm font-medium px-2 py-1 rounded bg-green-100 text-green-700">
                    {suggestedBin.current_qty}/{suggestedBin.capacity}
                  </span>
                </div>
              )}

              <div>
                <Label>Bin Location</Label>
                <Input
                  ref={binInputRef}
                  value={binCode}
                  onChange={(e) => setBinCode(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleConfirmPack)}
                  placeholder="Scan or override bin"
                  className="mt-1"
                  data-testid="bin-code-input"
                />
              </div>
            </div>
          </div>
        )}

        {bundleDetails && packBarcode && binCode && (
          <Button
            onClick={handleConfirmPack}
            disabled={loading || packSuccess}
            className={`w-full h-14 text-lg ${
              packSuccess ? 'bg-green-500 hover:bg-green-500' : 'bg-[#2D5F4F] hover:bg-[#234A3D]'
            } text-white`}
            data-testid="putaway-pack-btn"
          >
            {packSuccess ? (
              <>
                <CheckCircle className="w-6 h-6 mr-2" />
                Pack Stored!
              </>
            ) : (
              <>
                <Box className="w-6 h-6 mr-2" />
                Confirm Pack Put-Away
              </>
            )}
          </Button>
        )}
      </main>
    </div>
  );
};

export default PerSectionFgPutAwayClone;
